import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import { dbHelpers } from '../config/database';

interface User {
  id: string;
  name: string;
  email: string;
  role_id: number;
  department: string;
  created_at: string;
  approval_status: string;
}

const PendingApprovals = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await dbHelpers.getAllUsers();
      console.log('getAllUsers response:', res);
      
      if (!res || res.status === false) {
        alert('API Error: ' + (res?.message || 'Unknown error'));
        setUsers([]);
        setLoading(false);
        return;
      }
      
      const allUsers = Array.isArray(res?.data) ? res.data : [];
      console.log('All users:', allUsers);
      
      const pending = allUsers.filter((u: any) => u.approval_status === 'pending');
      console.log('Pending users:', pending);
      
      setUsers(pending);
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Error loading users: ' + (error instanceof Error ? error.message : 'Unknown'));
    }
    setLoading(false);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleApprove = async (id: string) => {
    try {
      const res = await dbHelpers.approveUser(id);
      console.log('Approve response:', res);
      
      if (res?.status) {
        alert('✅ User approved!');
        await loadUsers();
      } else {
        alert('❌ ' + (res?.message || 'Error approving user'));
      }
    } catch (error) {
      alert('Error: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleReject = async (id: string) => {
    if (!confirm('Reject this user?')) return;
    
    try {
      const res = await dbHelpers.rejectUser(id);
      console.log('Reject response:', res);
      
      if (res?.status) {
        alert('❌ User rejected!');
        await loadUsers();
      } else {
        alert('❌ ' + (res?.message || 'Error rejecting user'));
      }
    } catch (error) {
      alert('Error: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Pending User Approvals</h1>
      
      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : users.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No pending users</p>
      ) : (
        <div className="space-y-4">
          {users.map((user) => (
            <div
              key={user.id}
              className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            >
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-semibold text-lg">{user.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p>{user.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Department</p>
                  <p>{user.department}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Applied</p>
                  <p>{new Date(user.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => handleApprove(user.id)}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition"
                >
                  <CheckCircle className="w-5 h-5" />
                  Approve
                </button>
                <button
                  onClick={() => handleReject(user.id)}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition"
                >
                  <XCircle className="w-5 h-5" />
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PendingApprovals;
